import api from "@/lib/apiClient";

/**
 * The remote backend's GET /api/orders list payload does NOT include the
 * nested `items[]` array (only summary fields like item_count, completed_count).
 * Pages that derive scan activity / per-stage counts (Wallboard, Productivity,
 * Dashboard activity feed) need the full items+progress data.
 *
 * This helper:
 *   1. fetches active + completed lists in parallel,
 *   2. fans out one detail fetch per order to pull items[],
 *   3. returns an array of orders enriched with items.
 *
 * Errors on individual detail fetches are swallowed — that order will simply
 * appear without items, but the rest of the wallboard / leaderboard still
 * renders.
 */
export async function fetchOrdersWithItems({ signal } = {}) {
  const [activeRes, completedRes] = await Promise.all([
    api.get("/orders", { params: { status: "active" }, signal }).catch(() => ({ data: [] })),
    api.get("/orders", { params: { status: "completed" }, signal }).catch(() => ({ data: [] })),
  ]);
  const byId = new Map();
  for (const o of (activeRes.data || [])) if (o?.id) byId.set(o.id, o);
  for (const o of (completedRes.data || [])) if (o?.id) byId.set(o.id, o);

  const ids = Array.from(byId.keys());
  if (ids.length === 0) return [];

  // Fan out detail fetches in parallel. HTTP/2 multiplexes; ~50 simultaneous
  // requests complete in well under 2s in practice and Cloudflare won't
  // rate-limit a single client at this volume.
  const detailResults = await Promise.allSettled(
    ids.map((id) => api.get(`/orders/${id}`, { signal }).then((r) => r.data).catch(() => null)),
  );
  detailResults.forEach((res, i) => {
    if (res.status === "fulfilled" && res.value && Array.isArray(res.value.items)) {
      const id = ids[i];
      const existing = byId.get(id);
      byId.set(id, { ...existing, items: res.value.items });
    }
  });

  return Array.from(byId.values());
}
